import type { i18n as I18n } from 'i18next';
import { translateNamespacedLabel } from './resource-labels';

/**
 * Sportradar template ids such as `sr:exact_goals` must never appear on buttons.
 * Only the `sr:` prefix is stripped — other text (Handicap, Over/Under, `br:` keys,
 * numeric/`0:1` specifiers) is left alone.
 */
const SR_TOKEN_PATTERN = 'sr:[a-z][a-z0-9_]*(?:-[a-z0-9_]+)*';

function srTokenRegex(flags: string): RegExp {
  return new RegExp(SR_TOKEN_PATTERN, flags);
}

export function isSrTemplateToken(value: string): boolean {
  return new RegExp(`^${SR_TOKEN_PATTERN}$`, 'i').test(value.trim());
}

export function containsSrTemplateToken(value: string): boolean {
  return srTokenRegex('i').test(value);
}

/** Strip `sr:…` template ids and `{placeholders}` from a visible label. */
export function sanitizeOutcomeDisplay(label: string): string {
  return label
    .replace(srTokenRegex('gi'), '')
    .replace(/\{[^}]+\}/g, '')
    .replace(/\(\s*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+([)\].,!?:;])/g, '$1')
    .replace(/^[([\s]+|[)\]\s]+$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Value to show beside an outcome / in a market title placeholder.
 * Omits pure `sr:…` template ids; keeps handicap and totals (`2.5`, `hcp=-0.5`, `0:1`).
 */
export function displaySpecifierValue(specifier: string | null | undefined): string | undefined {
  const trimmed = specifier?.trim();
  if (!trimmed) return undefined;
  if (isSrTemplateToken(trimmed)) return undefined;

  // Drop `sr:…` fragments from compound strings, then clean empty key= leftovers.
  const withoutSr = trimmed
    .replace(srTokenRegex('gi'), '')
    .split('|')
    .map((part) => part.trim())
    .map((part) => {
      const separator = part.indexOf('=');
      if (separator <= 0) return part;
      const value = part.slice(separator + 1).trim();
      // `variant=` after an sr: value was stripped — drop the empty pair.
      if (!value) return '';
      return part;
    })
    .filter(Boolean);

  if (withoutSr.length === 0) return undefined;

  // Prefer a single key=value → show the value (`total=2.5` → `2.5`, `hcp=0:1` → `0:1`).
  if (withoutSr.length === 1) {
    const part = withoutSr[0]!;
    const separator = part.indexOf('=');
    if (separator > 0) {
      const value = part.slice(separator + 1).trim();
      return value || undefined;
    }
    return part || undefined;
  }

  // Multiple params: prefer a numeric / handicap-looking value, else first remaining.
  for (const part of withoutSr) {
    const separator = part.indexOf('=');
    const value = (separator > 0 ? part.slice(separator + 1) : part).trim();
    if (value && /^[+-]?\d/.test(value)) return value;
  }

  const first = withoutSr[0]!;
  const separator = first.indexOf('=');
  const value = (separator > 0 ? first.slice(separator + 1) : first).trim();
  return value || undefined;
}

function resolvedTranslation(
  key: string,
  i18n: I18n,
  feedType?: number | null,
): string | undefined {
  const translated = translateNamespacedLabel(key, i18n, 'outcome', feedType);
  // Identity fallback means the dictionary had no entry — treat as unresolved.
  if (!translated || translated === key) return undefined;

  const cleaned = sanitizeOutcomeDisplay(translated);
  if (!cleaned || isSrTemplateToken(cleaned)) return undefined;
  return cleaned;
}

/**
 * Resolve an outcome code (e.g. `Over` / `br:1`) to its OutcomeTranslates label.
 * `sr:…` template ids are never returned as visible text.
 */
export function translateOutcomeLabel(
  key: string,
  i18n: I18n,
  feedType?: number | null,
): string {
  const trimmed = key.trim();
  if (!trimmed) return trimmed;

  if (isSrTemplateToken(trimmed)) {
    const bare = trimmed.replace(/^sr:/i, '');
    return (
      resolvedTranslation(trimmed, i18n, feedType) ??
      resolvedTranslation(bare, i18n, feedType) ??
      ''
    );
  }

  return resolvedTranslation(trimmed, i18n, feedType) ?? sanitizeOutcomeDisplay(trimmed);
}
